import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useModal } from 'react-brave-modal';
import { toast } from 'react-toastify';
import { format, parseISO } from 'date-fns';

import Button from '../../../../components/Button';
import { useAuth } from '../../../../hooks/auth';
import { AlertRow } from '../../../../entities/Alert';
import { createAttendance } from '../../../../services/appointments/CreateAttendance';
import { treatAlert } from '../../../../services/alerts';
import getAlertLabels from '../../../../utils/getAlertLabels';
import AppointmentTypeEnum from '../../../../enums/AppointmentTypeEnum';
import AppointmentAreaEnum from '../../../../enums/AppointmentAreaEnum';
import OccupationAreaEnum from '../../../../enums/OccupationAreaEnum';

import {
  Actions,
  Badges,
  Badge,
  Container,
  Explanation,
  TypeChoice,
  TypeOption,
  Summary,
  SummaryRow,
} from './styles';

interface TreatModalProps {
  alert: AlertRow;
  /** Chamado depois de o alerta sair da fila, para a tela recarregar. */
  onTreated(): void;
}

/**
 * Por que o usuário não pode tratar. Um texto só para a tabela, o informativo e a
 * guarda daqui, que fica como segunda tranca.
 */
export const CANNOT_TREAT_MESSAGE =
  'Seu usuário não tem cadastro de profissional; não é possível abrir atendimento.';

const formatDateTime = (value: string): string =>
  format(parseISO(value), "dd/MM/yyyy 'às' HH:mm");

/** Os tipos que abrem sala de atendimento. SMS nao entra: alerta pede contato. */
const TYPES = [
  { value: AppointmentTypeEnum.PHONE, label: 'Ligação' },
  { value: AppointmentTypeEnum.VIDEO, label: 'Videochamada' },
];

/**
 * "Tratar alerta" — a acao que faltava na home de Alertas (tarefa 4.8/5.2).
 *
 * ## Por que ela cria o atendimento aqui, e nao no back
 *
 * `03-prontuario.md` bloco 7 diz que "Tratar alerta" **sempre abre um atendimento**,
 * e o `POST /alerts/{id}/treat` exige um `appointmentId` justamente para que nao
 * exista caminho de tratar sem um. Fazer a rota criar o atendimento quando ele
 * faltasse quebraria essa garantia e daria ao sistema uma segunda forma de criar
 * atendimento, paralela ao `POST /attendances` que ja existe.
 *
 * Entao a ordem e: **cria o atendimento, vincula, abre a sala**. Se o vinculo
 * falhar, o atendimento ja existe e a pessoa e avisada — ele fica no historico do
 * paciente como qualquer atendimento avulso, e o alerta continua na fila. O
 * contrario (vincular antes de criar) e impossivel, e engolir o erro deixaria a
 * enfermeira achando que o alerta saiu da fila quando nao saiu.
 *
 * ⚠️ **A sala abre em aba nova**, como o "Atender agora" do prontuario, para a fila
 * continuar aberta atras: quem trata um alerta costuma voltar para o proximo.
 */
const TreatModal: React.FC<TreatModalProps> = ({ alert, onTreated }) => {
  const { user } = useAuth();
  const { closeModal } = useModal();
  const history = useHistory();

  const [type, setType] = useState<AppointmentTypeEnum>(
    AppointmentTypeEnum.PHONE,
  );
  const [isLoading, setIsLoading] = useState(false);

  const handleTreat = useCallback(async () => {
    if (!user?.professional_id) {
      // Acontece com usuario que nao tem registro de profissional: o back recusaria
      // com 404 de "Profissional nao encontrado", que nao explica nada a quem le.
      // Segunda tranca: a tela já não oferece "Tratar" a esse usuário.
      toast.error(CANNOT_TREAT_MESSAGE);

      return;
    }

    setIsLoading(true);

    const attendanceArea =
      user?.professional?.occupationArea === OccupationAreaEnum.MEDICINE
        ? AppointmentAreaEnum.MEDICINE
        : AppointmentAreaEnum.NURSING;

    let appointmentId: string | undefined;

    try {
      const appointment = await createAttendance({
        patientId: alert.patientId,
        professionalId: user.professional_id,
        attendanceType: type,
        attendanceArea,
        contactPhone: alert.whatsapp ?? undefined,
      });

      if (!appointment.id) {
        // `Appointment.id` e opcional no tipo do front (a entidade serve tambem
        // para o payload de criacao). Sem id nao ha o que vincular nem sala para
        // abrir, e seguir em frente daria "tratado" sem atendimento.
        throw new Error('Atendimento criado sem identificador.');
      }

      appointmentId = appointment.id;

      await treatAlert(alert.id, appointmentId);

      onTreated();
      closeModal();

      const tab = window.open(
        `/patient/attendance?id=${appointmentId}`,
        '_blank',
      );

      if (tab) {
        tab.focus();
      } else {
        // Bloqueador de pop-up: sem isto a pessoa clicaria em "Tratar", veria o
        // alerta sair da fila e nao teria como registrar nada.
        history.push(`/patient/attendance?id=${appointmentId}`);
      }
    } catch (error) {
      setIsLoading(false);

      const message = error.response?.data?.message;

      if (appointmentId) {
        toast.error(
          message ??
            'O atendimento foi aberto, mas não foi possível vinculá-lo ao alerta.',
        );
      } else {
        toast.error(message ?? 'Não foi possível abrir o atendimento.');
      }
    }
  }, [alert, closeModal, history, onTreated, type, user]);

  return (
    <Container>
      <h1>Tratar alerta</h1>

      <Explanation>
        Tratar um alerta <strong>abre um atendimento</strong>. Ao concluí-lo, o
        desfecho escolhido fecha este alerta.
      </Explanation>

      <Summary>
        <SummaryRow>
          <span>Paciente</span>
          {/* Só o ID: a tela é projetável em telão (regra 3 de `02-alertas.md`). */}
          <strong>{alert.patientId.slice(0, 8)}</strong>
        </SummaryRow>

        <SummaryRow>
          <span>Alerta</span>
          <Badges>
            <Badge color={getAlertLabels.severityColor(alert.severity)}>
              {getAlertLabels.severity(alert.severity)}
            </Badge>
            <Badge color={getAlertLabels.categoryColor(alert.category)}>
              {getAlertLabels.category(alert.category)}
            </Badge>
          </Badges>
        </SummaryRow>

        <SummaryRow>
          <span>Parâmetro</span>
          {/*
            Rótulo + leitura, não `alert.parameter` cru: desde 2026-08-28 o campo é a
            chave (`device_silence`), e a leitura formatada mora em `reading`.
          */}
          <strong>
            {getAlertLabels.parameter(alert.parameter)}
            {alert.reading ? ` · ${alert.reading}` : ''}
          </strong>
        </SummaryRow>

        {/*
          A leitura e a hora são o que abre a conversa com o paciente — sem elas a
          ligação começa sem o número na mão (achado 6 do QA de 18/09).
        */}
        <SummaryRow>
          <span>Aberto em</span>
          <strong>{formatDateTime(alert.occurredAt)}</strong>
        </SummaryRow>

        {/* Comparado já formatado: os instantes diferem em ms e repetiriam a hora. */}
        {formatDateTime(alert.lastTriggeredAt) !==
          formatDateTime(alert.occurredAt) && (
          <SummaryRow>
            <span>Última leitura</span>
            <strong>{formatDateTime(alert.lastTriggeredAt)}</strong>
          </SummaryRow>
        )}
      </Summary>

      <h2>Tipo de atendimento</h2>

      <TypeChoice>
        {TYPES.map((option) => (
          <TypeOption
            key={option.value}
            type="button"
            selected={type === option.value}
            onClick={() => setType(option.value)}
          >
            {option.label}
          </TypeOption>
        ))}
      </TypeChoice>

      <Actions>
        <Button
          buttonType="secondary"
          onClick={() => closeModal()}
          disabled={isLoading}
        >
          Cancelar
        </Button>

        <Button buttonType="primary" onClick={handleTreat} loading={isLoading}>
          Abrir atendimento
        </Button>
      </Actions>
    </Container>
  );
};

export default TreatModal;
